import React, { useState, useEffect, useRef } from 'react';
import { enrollInCourse } from './actions';
import { useTranslation } from 'react-i18next';
import { useCourseDetails } from '../../../shared/hooks/useCourseDetails';

interface CourseEnrollmentModalProps {
  courseId: number;
  onClose: () => void;
  onEnrollmentSuccess: () => void;
}

const CourseEnrollmentModal: React.FC<CourseEnrollmentModalProps> = ({ courseId, onClose, onEnrollmentSuccess }) => {
  const { t } = useTranslation();
  const { courseDetails, loading } = useCourseDetails(courseId);
  const [enrolling, setEnrolling] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isOpening, setIsOpening] = useState<boolean>(true);
  const [contentLoaded, setContentLoaded] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle entrance animation on component mount
  useEffect(() => {
    // Start with opening animation
    setIsOpening(true);

    // Use setTimeout instead of requestAnimationFrame to ensure consistent timing
    const timer = setTimeout(() => {
      setIsOpening(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle content load transition
  useEffect(() => {
    if (!loading && courseDetails) {
      const timer = setTimeout(() => {
        setContentLoaded(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setContentLoaded(false);
    }
  }, [loading, courseDetails]);

  // Handle close with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Handle outside click to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  /**
   * Handles the enrollment process for the course.
   */
  const handleEnrollment = async () => {
    if (!courseId) return;

    setEnrolling(true);
    const res = await enrollInCourse(courseId);
    setEnrolling(false);

    if (res) {
      onEnrollmentSuccess();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-opacity duration-300 ${
        isClosing ? 'bg-opacity-0' : isOpening ? 'bg-opacity-0' : 'bg-opacity-50'
      }`}
      style={{ backdropFilter: isOpening ? 'none' : 'blur(5px)', transition: 'backdrop-filter 0.3s ease-in-out' }}
    >
      <div
        ref={modalRef}
        className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden transition-all duration-300 ${
          isClosing
            ? 'opacity-0 scale-95 translate-y-4'
            : isOpening
              ? 'opacity-0 scale-95 translate-y-4'
              : 'opacity-100 scale-100 translate-y-0'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Modal header - Immediately visible */}
        <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-gray-700">
          <h2 id="modal-title" className="text-xl font-semibold text-gray-800 dark:text-white">
            {t('courses.courseDetails')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={t('common.close')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-600 dark:text-gray-300"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal content area */}
        <div className="flex-1 overflow-auto relative">
          {/* Skeleton loader - Show immediately when modal is visible and loading */}
          {loading && (
            <div className="p-6 space-y-4">
              <div className="animate-pulse">
                {/* Course image skeleton */}
                <div className="h-48 md:h-64 bg-gray-200 dark:bg-gray-700 rounded-xl mb-6"></div>

                {/* Title skeleton */}
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-full w-3/4 mb-6"></div>

                {/* Description skeleton - multiple lines */}
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-5/6"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-4/6"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-3/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-5/6"></div>
                </div>
              </div>
            </div>
          )}

          {/* Actual content - Only show when loaded */}
          {!loading && courseDetails && (
            <div className={`transition-all duration-500 ease-in-out ${contentLoaded ? 'opacity-100' : 'opacity-0'}`}>
              {/* Course image with gradient overlay */}
              <div className="p-6 pt-6 pb-0">
                <div className="relative w-full h-48 md:h-64 rounded-xl overflow-hidden mb-4">
                  <img
                    src={courseDetails?.cover?.url}
                    alt={courseDetails?.name || t('courses.courseImage')}
                    className="w-full h-full object-cover transition-transform hover:scale-105 duration-700"
                    onLoad={() => setContentLoaded(true)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
              </div>

              {/* Course details */}
              <div className="p-6 md:p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{courseDetails?.name || '-'}</h3>

                <div
                  className="prose dark:prose-invert max-w-none prose-img:rounded-xl prose-headings:font-semibold prose-a:text-primary"
                  dangerouslySetInnerHTML={{ __html: courseDetails?.description || '' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="p-5 border-t border-gray-100 dark:border-gray-700 flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
          >
            {t('app.cancel')}
          </button>
          <button
            disabled={loading || enrolling}
            onClick={handleEnrollment}
            className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            {enrolling ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('courses.enrolling')}
              </span>
            ) : (
              t('courses.enrollNow')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseEnrollmentModal;
